import { Injectable, inject, signal } from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { AlbumsSelector } from '@hunting-nature/Secacam/pages/gallery/albums/redux/album.reducer';
import { Store } from '@ngrx/store';
import { filter, map, distinctUntilChanged } from 'rxjs';

import { GeneralSelectors } from '../../redux/general/general.reducer';

/**
 * Service to manage tab bar visibility based on route data, platform, and overlay state.
 *
 * Native app: routes with `data: { showTabBar: true }` show the tab bar.
 * Web/Desktop: the tab bar stays visible on every page inside the home shell
 *
 * Example:
 * {
 *   path: 'cameras',
 *   component: CamerasPage,
 *   data: { showTabBar: true } // Tab bar visible on native (unless menu is open)
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TabBarVisibilityService {
  // services
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  private readonly isWebPlatform = !Capacitor.isNativePlatform();

  /**
   * Signal that emits true when tab bar should be visible.
   */
  readonly tabBarShown$$ = signal<boolean>(this.computeVisibility());

  constructor() {
    // Detect route changes and update signal
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.getRouteAllowsTabBar()),
        distinctUntilChanged()
      )
      .subscribe(() => this.tabBarShown$$.set(this.computeVisibility()));

    // Monitor side menu state changes
    this.store
      .select(GeneralSelectors.isMenuOpen)
      .pipe(distinctUntilChanged())
      .subscribe(() => this.tabBarShown$$.set(this.computeVisibility()));

    // Monitor fullscreen image viewer state changes
    this.store
      .select(AlbumsSelector.isImageViewerOpened)
      .pipe(distinctUntilChanged())
      .subscribe(() => this.tabBarShown$$.set(this.computeVisibility()));
  }

  private computeVisibility(): boolean {
    const menuIsOpen = this.store.selectSignal(GeneralSelectors.isMenuOpen)();
    const imageViewerOpen = this.store.selectSignal(AlbumsSelector.isImageViewerOpened)();

    return this.getRouteAllowsTabBar() && !menuIsOpen && !imageViewerOpen;
  }

  // Native apps opt in per-route via `showTabBar`; Web/Desktop shows the tab bar everywhere (excludes e.g. login, add-camera-flow).
  private getRouteAllowsTabBar(): boolean {
    return this.isWebPlatform ? this.isWithinHomeShell() : this.getRouteData();
  }

  /**
   * Get the showTabBar flag from the currently active route data.
   * Traverses the route tree to find the deepest child route.
   */
  private getRouteData(): boolean {
    // Get the root of the current router state
    let route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;

    // Navigate to the deepest activated child route
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Read showTabBar from route data, default to false if not specified
    return route.data['showTabBar'] === true;
  }

  // True while the active top-level route is the `home` shell (as opposed to e.g. login or add-camera-flow).
  private isWithinHomeShell(): boolean {
    return this.router.routerState.snapshot.root.firstChild?.routeConfig?.path === 'home';
  }
}
